/* eslint-disable react/jsx-no-useless-fragment */
import { KeyboardEvent, ReactElement, useCallback, useMemo, useState } from 'react';
import { useCAWContext } from '../CAW';
import classnames from 'classnames';
import { definitions, getRTL, getUniqueId, translate } from '@micro-apps/env';
import { useWindowSize } from '@fshr/hooks';
import { IconChevronRight } from '../../icon';
import TypeAhead, { type TypeAheadOption } from '../../type-ahead';
import BevSafeText from '../../bev-safe-text';

type Props = {
	step: number;
	setStep: (newStep: number) => void;
	destinationPlaceholder?: string;
	propertiesArray?: string[];
	updateSelection?: () => void;
};

export default function DestinationPicker({
	step,
	setStep,
	destinationPlaceholder,
	propertiesArray = [],
	updateSelection,
}: Props): ReactElement<any> {
	const [selectOpen, setSelectOpen] = useState(false);
	const { planStore, isPPC, isDesktop, isHeroVariant } = useCAWContext();
	const [width, height] = useWindowSize();
	const isTallViewport = isHeroVariant && (height || 0) >= 1024;
	const typeaheadid = useMemo(() => 'typeahead-' + getUniqueId(), []);
	const hasPropertiesArray = propertiesArray.length > 0;
	const sortedProperties = useMemo(() => {
		if (propertiesArray.length > 0) {
			const matched: NonNullable<definitions['region']['properties']> = [];
			propertiesArray.forEach(owsCode => {
				const prop = planStore?.sortedProperties?.find(
					property => property?.bookingConfig?.owsCode === owsCode
				);
				if (prop) matched.push(prop);
			});
			return matched;
		}
		return planStore?.sortedProperties ?? [];
	}, [propertiesArray, planStore?.sortedProperties]);
	const selectRegion = useCallback(
		(index: number) => {
			setStep(1 + (index + 1) * 0.1);
		},
		[setStep]
	);
	const selectProperty = useCallback(
		(hotelCode?: string) => {
			if (!hotelCode) return;
			planStore?.updateSearch({ hotelCode });
			setStep(2);
			updateSelection?.();
		},
		[planStore, setStep, updateSelection]
	);

	const mobileDestinationPickerClassName = classnames(
		'DestinationPicker',
		'mt-200',
		'[&_.DestinationPicker-item]:border-b [&_.DestinationPicker-item]:border-solid [&_.DestinationPicker-item]:border-border-subtle'
	);

	const mobileTypeaheadInputClassName = classnames(
		'TextInput-field TextSelect-field formElement-field formElement-field--labelOn',
		'!w-full !h-[50px] !border-[1px] !border-solid !border-border-subtle !bg-surface-form !rounded-[4px] !pl-[15px] !pr-[40px] !py-[12px] text-body2 !text-text-primary'
	);

	const mobileDestinationItemClassName =
		'DestinationPicker-item relative flex flex-col spacing-stack-xs h-full w-full min-h-0 items-start justify-center text-left outline-none focus:outline-none focus-visible:outline-none text-text-primary [.selected_&]:text-text-inverse-primary';

	const mobileDestinationItemRowClassName = classnames(
		'DestinationPicker-item-row',
		'flex w-full items-center justify-between',
		'[&_.icon]:relative [&_.icon]:shrink-0 [&_.icon]:text-[15px] [&_.icon]:pe-150',
		'rtl:[&_.icon]:left-0 rtl:[&_.icon]:right-auto rtl:[&_.icon]:pe-0 rtl:[&_.icon]:ps-150',
		'text-subtitle2',
		'!w-full'
	);

	const getPropertyItem = (property: definitions['propertyInfoContent'], i: number) => {
		return (
			<button
				key={i}
				className={classnames(mobileDestinationItemClassName, 'spacing-card-padding-y-xs')}
				onClick={() => {
					selectProperty(property?.bookingConfig?.owsCode);
				}}
			>
				<span className="PropertyTitle text-subtitle2">
					<BevSafeText>{property.shortName || property.officialName}</BevSafeText>
				</span>
				<span className="PropertySubtitle text-label text-text-secondary [.selected_&]:text-text-inverse-secondary">
					{property?.location?.enCountry && translate(property.location.enCountry)}
				</span>
			</button>
		);
	};
	const toPropertyOption = (
		property: definitions['propertyInfoContent'],
		i: number
	): TypeAheadOption | undefined => {
		const owsCode = property.bookingConfig?.owsCode;
		if (!owsCode) return undefined;
		return {
			label: property.shortName || property.officialName || '',
			value: owsCode,
			display: () => getPropertyItem(property, i),
		};
	};
	const propertyOptions: TypeAheadOption[] = (sortedProperties ?? []).flatMap((property, i) => {
		const option = toPropertyOption(property, i);
		return option ? [option] : [];
	});
	const regionProperties =
		step > 1 && step < 2
			? planStore?.regions?.[Math.round((step - 1) * 10) - 1]?.properties
			: undefined;
	const browseOptions: TypeAheadOption[] = hasPropertiesArray
		? propertyOptions
		: step === 1
			? (planStore?.regions ?? []).map((region, i) => ({
					label: region.name || '',
					value: `region:${i}`,
					display: () => (
						<button
							data-index={i}
							className={classnames(mobileDestinationItemClassName, 'spacing-card-padding-y-s')}
							onClick={() => selectRegion(i)}
						>
							<div className={mobileDestinationItemRowClassName}>
								<span className="RegionTitle font-bold">{region.name}</span>
								<IconChevronRight height={15} width={15} className="fill-current" />
							</div>
						</button>
					),
				}))
			: (regionProperties ?? []).flatMap((property, i) => {
					const option = toPropertyOption(property, i);
					return option ? [option] : [];
				});
	const onSelectOption = (option: TypeAheadOption) => {
		if (option.value.startsWith('region:')) {
			selectRegion(Number(option.value.slice('region:'.length)));
			return false;
		}
		selectProperty(option.value);
		return false;
	};
	const onKeyUp = useCallback(
		(event: KeyboardEvent<HTMLSelectElement>) => {
			if (event.key === 'Enter' || event.key === 'Space') {
				if (selectOpen) {
					setSelectOpen(false);
				} else {
					setSelectOpen(true);
					event.currentTarget.dispatchEvent(
						new MouseEvent('mousedown', {
							bubbles: true,
							cancelable: true,
							view: window,
						})
					);
				}
			}
		},
		[selectOpen, setSelectOpen]
	);
	const screenReaderDestText = translate('Destination');

	return (
		<>
			{isDesktop ? (
				<div
					className={classnames('DestinationPicker-container Dropdown-container', {
						col: !isPPC,
						'col-lg-12': isPPC,
						'w-full': isPPC || isTallViewport,
						'w-full max-w-[350px]': !isPPC && !isTallViewport,
						'xl:max-w-full xl:min-w-[350px]': isHeroVariant && !isTallViewport,
					})}
				>
					<div
						className={classnames('destinationPicker-container relative text-body3', {
							'w-full max-w-[350px]': !isTallViewport,
							'xl:max-w-full': isHeroVariant && !isTallViewport,
							'w-full': isPPC || isTallViewport,
						})}
					>
						<div
							className={classnames(
								'relative w-full text-[16px] flex flex-col justify-end spacing-stack-2xs justify-end'
							)}
						>
							<label
								htmlFor="destination-id"
								className="select__label absolute static !opacity-100 transition-none text-label"
								aria-label={screenReaderDestText}
							>
								<span
									className={isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'}
								>
									{screenReaderDestText}
								</span>
							</label>
							<span className="screen-reader-only">{screenReaderDestText}</span>
							<div className="relative">
								<select
									id="destination-id"
									className={classnames(
										'h-10 w-full appearance-none border-[1px] border-solid  rounded-[4px] pr-8 spacing-tag-padding-x leading-none outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-border-strong',
										{
											'text-text-secondary border-border-subtle bg-surface-form': !isHeroVariant,
											'text-text-inverse-heading border-border-inverse bg-img-overlay-40 backdrop-blur-[15px]':
												isHeroVariant,
										}
									)}
									defaultValue={planStore?.search?.hotelCode || 'find'}
									onChange={e => {
										let hotelCode: string | undefined = e.target.value;
										if (hotelCode === 'find') {
											hotelCode = undefined;
										}
										planStore?.updateSearch({ hotelCode });
									}}
									onKeyUp={onKeyUp}
								>
									<option
										value="find"
										defaultValue="find"
										disabled
										className="bg-surface text-text-secondary"
									>
										{destinationPlaceholder || translate('Find a Hotel or Resort')}
									</option>
									{sortedProperties?.map((property, i) => (
										<option
											key={i}
											value={property.bookingConfig?.owsCode}
											className="bg-surface text-text-secondary"
										>
											{property.shortName || property.officialName}
										</option>
									))}
								</select>
								<span
									aria-hidden="true"
									className={classnames(
										'pointer-events-none absolute right-3 top-1/2 h-[8px] w-[8px] -translate-y-1/2 rotate-45 border-r border-b',
										{
											'right-[unset] left-3': getRTL(),
											'border-text-primary': !isHeroVariant,
											'border-text-inverse-heading': isHeroVariant,
										}
									)}
								/>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className={mobileDestinationPickerClassName}>
					<label
						aria-label={screenReaderDestText}
						id={`${typeaheadid}-label`}
						htmlFor={typeaheadid}
						className="hide"
					>
						{screenReaderDestText}
					</label>
					<span className="screen-reader-only">{screenReaderDestText}</span>
					<TypeAhead
						id={typeaheadid}
						inputClassName={mobileTypeaheadInputClassName}
						placeholder={destinationPlaceholder || translate('Find your destination')}
						options={propertyOptions}
						browseOptions={browseOptions}
						keepListOpen
						interactiveResults
						clearResultsButton
						noResults={translate('No results')}
						onSelectOption={onSelectOption}
						wrapperClassName="flex flex-col gap-[10px] !pb-0 px-[15px]"
						className="relative !h-auto !border-0 !bg-transparent !px-0"
						iconClassName="absolute right-[33px] top-1/2 -translate-y-1/2 text-[16px] rtl:right-auto rtl:left-180"
						listClassName="static z-auto max-h-none overflow-visible bg-transparent m-0 list-none p-0"
						resultClassName="box-border !h-auto bg-transparent aria-selected:bg-surface-inverse"
					/>
				</div>
			)}
		</>
	);
}
